import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { LogOut, Undo2, AlertOctagon, Siren, EyeOff } from 'lucide-react';
import { EcarterDialog } from './EcarterDialog';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MOTIFS_ABANDON = [
  { value: 'raison_personnelle', label: 'Raison personnelle' },
  { value: 'sante', label: 'Santé' },
  { value: 'embauche', label: 'Embauche' },
  { value: 'reorientation', label: 'Réorientation' },
  { value: 'exclusion', label: 'Exclusion' },
  { value: 'autre', label: 'Autre' },
];
export const motifLabel = (m) => MOTIFS_ABANDON.find(x => x.value === m)?.label || m || '';
const fmt = (d) => (d ? format(parseISO(d.slice(0, 10)), 'dd/MM/yyyy') : '');

export const AbandonBadge = ({ abandon, className = '' }) => abandon ? (
  <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700 ${className}`} title={`${motifLabel(abandon.motif)}${abandon.commentaire ? ' — ' + abandon.commentaire : ''}`} data-testid="abandon-badge">
    <LogOut className="w-3 h-3" /> Abandon le {fmt(abandon.date)}
  </span>
) : null;

// Pré-remplit le commentaire avec le signalement du formateur (suspicion d'abandon)
const commentaireDepuisSuspicion = (s) => s?.motif ? `Signalement de ${s.signale_par_nom || 'le formateur'} le ${fmt(s.date)} : ${s.motif}` : '';

export const AbandonDialog = ({ open, onOpenChange, dossierId, candidat, suspicion, onDone }) => {
  const [form, setForm] = useState({ date: format(new Date(), 'yyyy-MM-dd'), motif: 'raison_personnelle', commentaire: '' });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm(f => ({ ...f, commentaire: commentaireDepuisSuspicion(suspicion) })); }, [open, suspicion]);
  const submit = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/dossiers/${dossierId}/candidats/${candidat.id}/abandon`, form);
      toast.success('Abandon déclaré');
      onDone?.(res.data); onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="abandon-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><AlertOctagon className="w-5 h-5 text-red-500" /> Déclarer l'abandon de {candidat?.nom} {candidat?.prenom}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">À partir de la date indiquée, le candidat n'apparaîtra plus dans les journées du formateur et ne sera plus comptabilisé (assiduité, compétences, alertes). Ses données antérieures restent consultables.</p>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Date effective *</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="abandon-date-input" /></div>
          <div>
            <Label className="text-xs">Motif *</Label>
            <Select value={form.motif} onValueChange={(v) => setForm({ ...form, motif: v })}>
              <SelectTrigger data-testid="abandon-motif-select"><SelectValue /></SelectTrigger>
              <SelectContent>{MOTIFS_ABANDON.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div><Label className="text-xs">Commentaire</Label><Textarea rows={3} value={form.commentaire} onChange={(e) => setForm({ ...form, commentaire: e.target.value })} placeholder="Circonstances, démarches effectuées…" data-testid="abandon-commentaire-input" /></div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button className="bg-red-600 hover:bg-red-700" disabled={saving || !form.date} onClick={submit} data-testid="abandon-confirm-btn">Confirmer l'abandon</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const AbandonPanel = ({ dossierId, candidat, abandon, canManage, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [suspicion, setSuspicion] = useState(null);
  const [ecartees, setEcartees] = useState([]);
  const [ecarterOpen, setEcarterOpen] = useState(false);
  const [histoOpen, setHistoOpen] = useState(false);
  useEffect(() => {
    axios.get(`${API_URL}/dossiers/${dossierId}/candidats/${candidat.id}/suspicion-abandon`).then(r => { setSuspicion(r.data?.suspicion_abandon || null); setEcartees(r.data?.suspicions_ecartees || []); }).catch(() => {});
  }, [dossierId, candidat.id, abandon]);
  const ecarter = async (motif) => {
    try {
      const r = await axios.delete(`${API_URL}/dossiers/${dossierId}/candidats/${candidat.id}/suspicion-abandon`, { params: { motif } });
      setSuspicion(null); setEcartees(e => [...e, r.data.ecartee]); toast.success('Suspicion écartée — motif enregistré');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); throw e; }
  };
  const annuler = async () => {
    if (!window.confirm('Annuler la déclaration d\'abandon ? Le candidat sera de nouveau actif sur l\'action.')) return;
    try { await axios.delete(`${API_URL}/dossiers/${dossierId}/candidats/${candidat.id}/abandon`); toast.success('Abandon annulé'); onChanged?.(null); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };
  return (
    <Card className={`border-0 shadow-sm ${abandon ? 'ring-1 ring-red-200' : ''}`} data-testid="abandon-panel">
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><LogOut className={`w-4 h-4 ${abandon ? 'text-red-500' : 'text-indigo-500'}`} /> Abandon de la formation</h2>
            {abandon ? (
              <div className="mt-2 text-sm text-slate-700 space-y-1" data-testid="abandon-details">
                <p><span className="text-red-700 font-semibold">Abandon déclaré le {fmt(abandon.date)}</span> — {motifLabel(abandon.motif)}</p>
                {abandon.commentaire && <p className="text-slate-600 whitespace-pre-wrap">{abandon.commentaire}</p>}
                <p className="text-xs text-slate-400">Déclaré par {abandon.declare_par_nom || '—'} le {fmt(abandon.declare_le)}</p>
              </div>
            ) : (
              <p className="text-xs text-slate-400 mt-1">Candidat actif sur l'action.{!canManage && ' En cas de risque d\'abandon, utilisez la chip « Risque d\'abandon » dans les observations : le responsable pédagogique sera alerté.'}</p>
            )}
          </div>
          {canManage && (abandon
            ? <Button variant="outline" size="sm" onClick={annuler} data-testid="abandon-annuler-btn"><Undo2 className="w-3.5 h-3.5 mr-1" /> Annuler l'abandon</Button>
            : <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setOpen(true)} data-testid="abandon-declarer-btn"><LogOut className="w-3.5 h-3.5 mr-1" /> Déclarer un abandon</Button>)}
        </div>
        {suspicion && !abandon && (
          <div className="mt-4 flex items-start justify-between gap-3 flex-wrap rounded-lg border border-orange-200 bg-orange-50 px-4 py-3" data-testid="suspicion-abandon-banner">
            <div className="text-sm">
              <p className="font-semibold text-orange-800 flex items-center gap-1.5"><Siren className="w-4 h-4" /> Suspicion d'abandon signalée par {suspicion.signale_par_nom} le {fmt(suspicion.date)}</p>
              <p className="text-orange-700 mt-0.5 whitespace-pre-wrap">{suspicion.motif}</p>
            </div>
            {canManage && (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setEcarterOpen(true)} data-testid="suspicion-ecarter-btn"><EyeOff className="w-3.5 h-3.5 mr-1" /> Écarter</Button>
                <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={() => setOpen(true)} data-testid="suspicion-confirmer-btn"><LogOut className="w-3.5 h-3.5 mr-1" /> Confirmer l'abandon</Button>
              </div>
            )}
          </div>
        )}
        {ecartees.length > 0 && (
          <div className="mt-3 text-xs" data-testid="suspicions-ecartees">
            <button type="button" onClick={() => setHistoOpen(o => !o)} className="text-slate-500 hover:text-slate-800 underline" data-testid="suspicions-ecartees-toggle">{ecartees.length} suspicion(s) d'abandon écartée(s) — {histoOpen ? 'masquer' : 'voir le détail'}</button>
            {histoOpen && (
              <ul className="mt-2 space-y-2">
                {[...ecartees].reverse().map((e, i) => (
                  <li key={i} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2" data-testid={`suspicion-ecartee-${i}`}>
                    <p className="text-slate-700"><span className="font-semibold">Signalée le {fmt(e.date)}</span> par {e.signale_par_nom} : <span className="whitespace-pre-wrap">{e.motif}</span></p>
                    <p className="text-slate-600 mt-1"><EyeOff className="w-3 h-3 inline mr-1" /><span className="font-semibold">Écartée le {fmt(e.ecarte_le)}</span> par {e.ecarte_par_nom} : <span className="whitespace-pre-wrap italic">{e.motif_ecart}</span></p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        {suspicion && (
          <EcarterDialog open={ecarterOpen} onOpenChange={setEcarterOpen} titre="Écarter la suspicion d'abandon" onConfirm={ecarter} testId="suspicion-ecarter-dialog"
            cause={<><p><span className="font-semibold">Signalée par {suspicion.signale_par_nom}</span> le {fmt(suspicion.date)}</p><p className="whitespace-pre-wrap">{suspicion.motif}</p></>} />
        )}
        <AbandonDialog open={open} onOpenChange={setOpen} dossierId={dossierId} candidat={candidat} suspicion={suspicion} onDone={(a) => { setSuspicion(null); onChanged?.(a); }} />
      </CardContent>
    </Card>
  );
};
